/* eslint-disable compat/compat */
import axios from 'axios'

const name = process.env.npm_package_name
const version = process.env.npm_package_version

function createGqlApi() {
  return {
    async get(query, url) {
      const { data, errors } = await axios({
        method: 'POST',
        url,
        data: { query, variables: null },
        timeout: 7500,
        headers: {
          'User-Agent': `${name}/${version} (node/isomorphic-fetch)`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      }).then(res => res.data)
      if (errors && errors.length) {
        throw Error(errors)
      }
      return transformResponse(data)
    }
  }
}

export const gqlApi = createGqlApi()
export const gql = String.raw

function pngFragment(options) {
  const aspects = options.aspectRatios
    .map(ratio => {
      const sizes = ratio.sizes
        .map(s => `w${s}: url(quality: ${ratio.quality || '0.7'}, width: ${s}, format: png)`)
        .join('\n')

      return `${ratio.id}: crop(aspect: ${ratio.id}) {
        ${ratio.id === 'original' ? 'aspect' : ''}
        ${sizes}
      }`
    })
    .join('\n')

  return `
fragment imageCropsPng on Image {
  title,
  creators,
  credit,
  category,
  ${aspects}
}
`
}

function jpgFragment(options) {
  const aspects = options.aspectRatios
    .map(ratio => {
      const sizes = ratio.sizes
        .map(size => `w${size}: url(quality: ${ratio.quality || '0.7'}, width: ${size})`)
        .join('\n')

      return `${ratio.id}: crop(aspect: ${ratio.id}) {
        ${ratio.id === 'original' ? 'aspect' : ''}
        ${sizes}
      }`
    })
    .join('\n')

  return `
fragment imageCropsJpg on Image {
  title,
  creators,
  credit,
  category,
  ${aspects}
}
`
}
/**
 * Transforms a series of image sections to a Polopoly GraphQL API query
 */

export function createQuery(imageObjects, options) {
  const images = getImageIds(imageObjects)
  return `
    ${createBase(images, options)}
    query {
      ${images.map(getImage).join('\n')}
    }`
}

const IMAGE_PROPS = ['vertical', 'horizontal']

function getImageIds(imageObjects) {
  if (imageObjects.some(image => !image)) {
    throw new Error('Undefined image object')
  }
  return imageObjects.reduce((result, image) => {
    const format = image.format || (image.advanced && image.advanced.format) || 'jpg'

    /* Image can be stored directly on image object */
    if (image.id && !result.find(el => el.id === image.id)) {
      result.push({ id: image.id, format })
    }

    /* or on a predefined set of image properties */
    IMAGE_PROPS.forEach(prop => {
      const value = image[prop]
      if (value && value.id && !result.find(el => el.id === value.id)) {
        result.push({ id: value.id, format })
      }
    })
    return result
  }, [])
}

function getImage({ id, format = 'jpg' }) {
  if (format === 'png') {
    return `${transformId(id)}: model(id: "${id}") {
      ...imageCropsPng
    }`
  }
  return `${transformId(id)}: model(id: "${id}") {
    ...imageCropsJpg
  }`
}

function createBase(imageObjects, options) {
  const containsJpg = imageObjects.filter(el => el.format === 'jpg').length > 0
  const containsPng = imageObjects.filter(el => el.format === 'png').length > 0
  return `
    ${containsJpg ? jpgFragment(options) : ''}
    ${containsPng ? pngFragment(options) : ''}
  `
}

/**
 * Transforns a polopoly id to something that can be used as a key in a GraphQL query
 * Example: '1.14252981' -> 'id_1_14252981'
 */
function transformId(id) {
  return `id${id.replace(/\./g, '__')}`
}

/**
 * Transforns an already transformed polopoly id back to a regular polopoly id
 * Example: ''id_1_14252981' -> 1.14252981'
 */
function transformBackId(id) {
  return id.replace('id', '').replace(/__/g, '.')
}

function transformResponse(response) {
  return Object.keys(response).reduce((res, key) => {
    const transformedKey = transformBackId(key)
    res[transformedKey] = response[key]
    return res
  }, {})
}
